import { Check, Copy, TriangleAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button } from '@aeris/ui'

/**
 * A entrega de uma credencial: mostrada uma vez, irreproduzível.
 *
 * Extraído de `platform-account-form.tsx` na F121, quando a redefinição de
 * senha do dono passou a precisar exatamente do mesmo painel. Duplicá-lo custaria
 * mais do que parece: o aviso de "aparece uma única vez", o `select-all`, o
 * fallback de área de transferência fora de contexto seguro e o
 * `data-testid="temporary-password"` que os testes e o e2e procuram teriam que
 * ser mantidos em sincronia em dois arquivos — e a cópia que ficasse para trás
 * seria descoberta por alguém que perdeu uma senha.
 *
 * `intro` é o que muda entre os dois usos: criar uma conta e devolver o acesso
 * de alguém que se perdeu são frases diferentes para a mesma entrega.
 */

export const CredentialHandover = ({
  intro,
  email,
  password,
  onClose,
}: {
  /** A frase que explica ESTA entrega. O aviso de "uma única vez" é fixo e vem depois. */
  intro: ReactNode
  email: string
  password: string
  onClose: () => void
}): ReactNode => {
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <p
        role="alert"
        className="flex items-start gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)"
      >
        <TriangleAlert
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
        />
        <span>
          {intro} Esta senha aparece <strong>uma única vez</strong>: ela não
          fica guardada e não pode ser consultada depois. O sistema exige a
          troca no primeiro acesso.
        </span>
      </p>

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) text-(--text-muted)">
          E-mail
        </span>
        <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-primary)">
          {email}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) text-(--text-muted)">
          Senha temporária
        </span>
        <div className="flex items-center gap-2">
          <code
            data-testid="temporary-password"
            className="flex-1 select-all rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) px-3 py-2 font-(--font-mono) text-(length:--text-sm) tracking-wider text-(--text-primary)"
          >
            {password}
          </code>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leadingIcon={
              copied ? (
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Copy aria-hidden="true" className="h-3.5 w-3.5" />
              )
            }
            onClick={() => {
              // Melhor esforço: a área de transferência não existe fora de um
              // contexto seguro. A senha continua na tela e `select-all` faz a
              // seleção em um clique, então a falha custa um botão, não a
              // entrega.
              try {
                void navigator.clipboard.writeText(password).then(
                  () => {
                    setCopied(true)
                  },
                  () => {
                    setCopied(false)
                  }
                )
              } catch {
                setCopied(false)
              }
            }}
          >
            {copied ? 'Copiada' : 'Copiar'}
          </Button>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <Button type="button" variant="primary" onClick={onClose}>
          Já anotei
        </Button>
      </div>
    </div>
  )
}
